import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../components/ui/table';
import { FolderKanban, ChevronRight } from 'lucide-react';

export default function ProjectsPage() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const api = authAxios();
        const res = await api.get('/dashboard/by-project');
        setProjects(res.data);
      } catch {
        toast.error('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500" data-testid="projects-loading">Loading projects...</div>;
  }

  return (
    <div data-testid="projects-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Projects</h1>
        <p className="text-sm text-slate-500 mt-1">View all projects and their manpower status</p>
      </div>

      <Card className="border-slate-200" data-testid="projects-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-4">Project</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Area</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Required</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Actual</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Gap</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Open Cases</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map(p => (
                <TableRow
                  key={p.project_id}
                  className="hover:bg-slate-50 cursor-pointer border-b border-slate-100"
                  onClick={() => navigate(`/projects/${p.project_id}`)}
                  data-testid={`project-row-${p.project_id}`}
                >
                  <TableCell className="px-4">
                    <div className="flex items-center gap-2">
                      <FolderKanban className="h-4 w-4 text-slate-400" />
                      <span className="text-sm font-medium text-slate-800">{p.project_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{p.area}</TableCell>
                  <TableCell className="text-sm text-slate-600 text-center font-mono">{p.required}</TableCell>
                  <TableCell className="text-sm text-slate-600 text-center font-mono">{p.actual}</TableCell>
                  <TableCell className="text-center">
                    {p.gap > 0 ? (
                      <Badge className="bg-red-50 text-red-700 border-red-200 font-mono">{p.gap}</Badge>
                    ) : (
                      <Badge className="bg-green-50 text-green-700 border-green-200 font-mono">0</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 text-center font-mono">{p.open_cases}</TableCell>
                  <TableCell>
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
